/**
 * Validates each item against 8 actions based on status + permissions.
 *
 * Returns both flags (for priority strategies) and pre-filtered arrays (for commands).
 * Commands use the arrays directly to avoid re-validation.
 * O(n × 8) cost - this is the expensive computation that gets cached.
 */
import { useCallback } from 'react'

import { Section } from 'modules/expenses/lib/RouteContextParams'
import {
  canAddExpensableToPayment,
  canAddExpensableToPayroll,
  canRemoveExpensableFromPayroll,
  canRestartExpensable,
  canRevertPaidExpensable,
  canReviewExpensable,
  canSetToPaidExpensable,
} from 'modules/expenses/lib/expensables/availableActions'

import { ActionEligibility, Expensable } from './types'
import { createEligibilityDefaults, isNonActionableStatus } from './utils'

interface UseActionEligibilityProps {
  canSyncWithPayroll: boolean
  canSyncWithBanking: boolean
  canRestartApprovalFlow: boolean
  canSetExpensableToPaid: boolean
  section?: Section
}

export const useActionEligibility = ({
  canSyncWithPayroll,
  canSyncWithBanking,
  canRestartApprovalFlow,
  canSetExpensableToPaid,
  section,
}: UseActionEligibilityProps) => {
  const computeEligibility = useCallback(
    (expensables: Expensable[]): ActionEligibility => {
      if (expensables.length === 0) {
        return createEligibilityDefaults()
      }

      const eligibility = expensables.reduce((acc, exp) => {
        if (canReviewExpensable(exp, section)) {
          acc.canApprove = true
          acc.canReject = true
          acc.eligibleForApproval.push(exp)
          acc.eligibleForRejection.push(exp)
        }

        if (canAddExpensableToPayroll(exp, canSyncWithPayroll)) {
          acc.canReimbursePayroll = true
          acc.eligibleForPayroll.push(exp)
        }

        if (canAddExpensableToPayment(exp, canSyncWithBanking)) {
          acc.canReimburseSepa = true
          acc.eligibleForSepa.push(exp)
        }

        if (canSetToPaidExpensable(exp, canSetExpensableToPaid, section)) {
          acc.canSetPaid = true
          acc.eligibleForSetPaid.push(exp)
        }

        if (canRestartExpensable(exp, canRestartApprovalFlow, section)) {
          acc.canResetApproval = true
          acc.eligibleForResetApproval.push(exp)
        }

        if (canRemoveExpensableFromPayroll(exp, canSyncWithPayroll)) {
          acc.canRemovePayroll = true
          acc.eligibleForRemovePayroll.push(exp)
        }

        if (canRevertPaidExpensable(exp, canSetExpensableToPaid, section)) {
          acc.canRevertPaid = true
          acc.eligibleForRevertPaid.push(exp)
        }

        if (!isNonActionableStatus(exp.status)) {
          acc.allNonActionable = false
        }

        return acc
      }, createEligibilityDefaults())

      return eligibility
    },
    [
      canSyncWithPayroll,
      canSyncWithBanking,
      canRestartApprovalFlow,
      canSetExpensableToPaid,
      section,
    ]
  )

  return { computeEligibility }
}
